import React from "react";
import { CampaignStatusAttachment } from "../types";

interface CampaignProgressCardProps {
  data: CampaignStatusAttachment;
  onCancel?: (campaignId: string) => void;
  onCheckReplies?: (campaignId: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Queued",
  sending: "Sending",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "text-amber-600 dark:text-amber-400",
  sending: "text-blue-600 dark:text-blue-400",
  completed: "text-green-600 dark:text-green-400",
  cancelled: "text-theme-fg-muted",
};

function timeAgo(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const CampaignProgressCard: React.FC<CampaignProgressCardProps> = ({
  data,
  onCancel,
  onCheckReplies,
}) => {
  const { campaign_id, status, sent, total, failed, skipped, completed_at } = data;

  const isDone = status === "completed" || status === "cancelled";
  const progress = total > 0 ? Math.round((sent / total) * 100) : 0;
  const remaining = total - sent - failed - skipped;

  const headerIconStyle: React.CSSProperties = {
    background:
      status === "completed"
        ? "color-mix(in srgb, #22c55e 18%, transparent)"
        : status === "cancelled"
        ? "color-mix(in srgb, var(--color-fg) 10%, transparent)"
        : "color-mix(in srgb, var(--color-primary) 16%, transparent)",
  };

  return (
    <div className="my-3 w-full min-w-0 max-w-full rounded-2xl border border-theme-border bg-theme-surface-ui p-4">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-4">
        <span
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
          style={headerIconStyle}
        >
          {isDone ? (
            <span
              className={`material-icons text-[18px] ${
                status === "completed" ? "text-green-600 dark:text-green-400" : "text-theme-fg-muted"
              }`}
            >
              {status === "completed" ? "check_circle" : "cancel"}
            </span>
          ) : (
            <span className="material-icons text-[18px] text-theme-primary">send</span>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-theme-fg leading-tight">
              Outreach Campaign
            </p>
            <span className={`text-xs font-medium ${STATUS_COLORS[status] ?? "text-theme-fg-muted"}`}>
              · {STATUS_LABELS[status] ?? status}
            </span>
          </div>
          <p className="text-xs text-theme-fg-muted mt-0.5 truncate">
            {total} {total === 1 ? "recipient" : "recipients"}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {!isDone ? (
        <div className="mb-3">
          <div className="flex justify-between text-xs text-theme-fg-muted mb-1">
            <span>{sent} sent</span>
            <span>{total} total</span>
          </div>
          <div className="relative h-2 w-full rounded-full bg-theme-surface overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-theme-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-theme-fg-muted mt-1 text-right">{progress}%</p>
        </div>
      ) : null}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="flex flex-col items-center rounded-xl border border-theme-border bg-theme-surface py-2">
          <span className="text-lg font-bold text-theme-fg leading-none">{sent}</span>
          <span className="text-xs text-theme-fg-muted mt-0.5">sent</span>
        </div>
        <div className="flex flex-col items-center rounded-xl border border-theme-border bg-theme-surface py-2">
          <span
            className={`text-lg font-bold leading-none ${
              failed > 0 ? "text-red-600 dark:text-red-400" : "text-theme-fg"
            }`}
          >
            {failed}
          </span>
          <span className="text-xs text-theme-fg-muted mt-0.5">failed</span>
        </div>
        <div className="flex flex-col items-center rounded-xl border border-theme-border bg-theme-surface py-2">
          <span className="text-lg font-bold text-theme-fg leading-none">{skipped}</span>
          <span className="text-xs text-theme-fg-muted mt-0.5">skipped</span>
        </div>
      </div>

      {/* Remaining (in-progress) */}
      {!isDone && remaining > 0 && (
        <p className="text-xs text-theme-fg-muted mb-3">
          {remaining} remaining in queue
        </p>
      )}

      {/* Completed timestamp */}
      {completed_at && (
        <p className="text-xs text-theme-fg-muted mb-3">
          {status === "cancelled" ? "Cancelled" : "Completed"} {timeAgo(completed_at)}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {!isDone && onCancel && (
          <button
            onClick={() => onCancel(campaign_id)}
            className="flex-1 rounded-xl border border-theme-border bg-theme-surface py-1.5 text-sm font-medium text-theme-fg hover:bg-theme-surface-ui transition-colors"
          >
            Cancel remaining
          </button>
        )}
        {status === "completed" && onCheckReplies && (
          <button
            onClick={() => onCheckReplies(campaign_id)}
            className="flex-1 rounded-xl bg-theme-primary py-1.5 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            Check for replies
          </button>
        )}
      </div>
    </div>
  );
};
